import "./style.scss";

import { Button } from "@material-ui/core";
import Loader from "@material-ui/core/CircularProgress";
import { withFormik } from "formik";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import HTTP from "../../api/http";
import { EntryText } from "../../components/Entry";
import EventForm from "../../components/EventForm";
import SVG from "../../components/Svg";
import useQuery from "../../hooks/useQuery";
import { getDefaults } from "../../utils/defaults";
import slugify from "../../utils/slugify";
// ...
import { getNewDates, valSchemaInfo as validationSchema } from "./entries";
const iniValues = validationSchema.cast();


//? ============================================================================
//? LE PASSAGE DE SESSION SE LIT DANS LE FRAGMENT, PAS DANS LA CHAÎNE DE REQUÊTE.
//? ============================================================================
//? Ce module n'a aucune authentification à lui : sa seule porte d'entrée est un
//? JWT fourni par l'application appelante. `evey.live` le passe en clair dans la
//? chaîne de requête (`libs/links.js` : `?token=<jwt>&eventId=<id>`), et ce jeton
//? part alors dans TROIS endroits qu'on ne contrôle pas :
//?
//?   1. l'historique du navigateur,
//?   2. les journaux de tout ce qui sert cette page — un jeton de session complet
//?      en clair dans une ligne de log,
//?   3. l'en-tête `Referer` envoyé à chaque ressource tierce chargée ensuite.
//?
//? UN FRAGMENT N'EST JAMAIS TRANSMIS AU SERVEUR. Les points 2 et 3 disparaissent
//? donc entièrement, et le 1 est refermé plus bas en retirant le fragment de la
//? barre d'adresse dès qu'il est lu — avant le moindre appel réseau.
//?
//? ⚠️ LA CHAÎNE DE REQUÊTE RESTE ACCEPTÉE, et ce n'est pas une négligence :
//? `evey.live` est toujours en production et continue d'envoyer cette forme. La
//? retirer couperait ses organisateurs de ce module du jour au lendemain. Le
//? fragment est donc PRIORITAIRE, la chaîne de requête est le repli hérité.
//?
//? Lu au CHARGEMENT DU MODULE, pas dans un effet : le fragment doit être capturé
//? et effacé avant que quoi que ce soit d'autre ne puisse naviguer ou le lire.
//?
//? Le routeur est un `createBrowserHistory` (`src/App.js:24`) — VÉRIFIÉ avant
//? d'écrire ceci. Avec un routeur à fragment, le `#` porterait la route et cette
//? approche casserait la navigation.
const HANDOFF = (() => {
  if (typeof window === "undefined") return {};

  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : "";
  if (!raw) return {};

  const frag = new URLSearchParams(raw);
  const picked = {
    token: frag.get("token"),
    eventId: frag.get("eventId"),
    eventNew: frag.get("eventNew"),
    goToReport: frag.get("goToReport"),
  };

  //? On n'efface que si le fragment portait bien un passage de session — sinon
  //? on réécrirait une URL qui ne nous concerne pas.
  if (picked.token || picked.eventId || picked.eventNew) {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
    return picked;
  }

  return {};
})();

const EventAuth = (props) => {
  const query = useQuery();
  const history = useHistory();
  const dValues = getDefaults();
  // ...
  const [isNew, setIsNew] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  // ...
  const onStartHandler = async () => {
    setIsLoading(true);
    // ...
    //? Même priorité que dans `preAuth` : le fragment l'emporte sur la chaîne de
    //? requête. Sans cette ligne, créer un événement depuis `evey.next` écrirait
    //? `null` en session et l'appel suivant partirait sans jeton.
    const THE_JWT = HANDOFF.token || query.get("token");
    window.localStorage.setItem("THE_JWT", THE_JWT);
    // ...
    const title = props.values?.title || "";
    const slug = slugify(title);
    const { startDate, endDate } = getNewDates();
    // ...
    const data = {
      title,
      slug,
      startDate,
      endDate,
      // ...
      // access: "public",
      category: "event",
      type: "virtual",
      // ...
      card: dValues.eventCard,
      cover: [dValues.eventCover],
    };
    // ...
    try {
      //# Create new event
      let res = await HTTP.POST("/organizations/events/create", data);
      window.localStorage.setItem("THE_ID", res.id);
      // ...
      setIsLoading(false);
      history.push("/event-setup");
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar("Error occured "+(err?.response?.data?.message || err?.response?.statusText), { variant: "error" });
      console.error("#CreateNewEvent :: ", err);
      //console.log("#err?.response :: ", err?.response);
    }
  };
  // ...
  const preAuth = async () => {
    //? FRAGMENT D'ABORD, chaîne de requête ensuite — voir le pavé en tête de
    //? fichier. `evey.next` n'envoie que la première forme ; `evey.live` que la
    //? seconde. Les deux entrent par ici.
    const NEW_IS = HANDOFF.eventNew || query.get("eventNew");
    const NEW_ID = HANDOFF.eventId || query.get("eventId");
    const NEW_JWT = HANDOFF.token || query.get("token");
    // ...
    const goToReport = HANDOFF.goToReport || query.get("goToReport");
    // ...
    const OLD_ID = window.localStorage.getItem("THE_ID");
    const OLD_JWT = window.localStorage.getItem("THE_JWT");
    // ...
    let THE_ID = NEW_ID || OLD_ID;
    let THE_JWT = NEW_JWT || OLD_JWT;

    //# NewEvent
    setIsNew(NEW_IS);
    if (NEW_IS) return;
    // ...
    if (THE_ID && THE_JWT) {
      window.localStorage.setItem("THE_ID", THE_ID);
      window.localStorage.setItem("THE_JWT", THE_JWT);
      // ...
      if (goToReport) history.push("/live-insights");
      else history.push("/event-setup");
    } else {
      const LIVE_URL = process.env.REACT_APP_EVEY_URL;
      window.location.href = `${LIVE_URL}/auth/login`;
    }
  };
  // ...
  useEffect(preAuth, []);
  // ...
  return (
    <div className="EventAuth">
      <div className="EventAuth__inner">
        <div className="EventAuth__content">
          <div className="EventAuth__content__inner">
            <div className="__top">
              <span className="__top__icon">
                <span className="__inner" />
              </span>
              <span style={{ color: "#fff", margin: "0 7px 0 0" }}>10000+</span>
              <span>event participants are watching</span>
            </div>
            <div className="__title">
              A seemless
              <div>
                Event <span style={{ color: "#00AB55" }}>Experience</span>
              </div>
            </div>
            <div className="__list">
              <div className="__list__item">
                <span className="__num">1</span>
                Build and publish your event in minutes
              </div>
              <div className="__list__item">
                <span className="__num">2</span>
                Explore real time event analytics and more
              </div>
            </div>

            {isNew && (
              <div className="__new">
                <div className="__new__title">Add your event name</div>
                <EventForm style={{ width: "500px" }}>
                  <EntryText
                    required
                    label="Event name"
                    name="title"
                    placeholder="Event name"
                    xs={12}
                    md={12}
                  />
                </EventForm>
              </div>
            )}

            <div className="__actions">
              <Button
                variant="contained"
                onClick={onStartHandler}
                disabled={isNew && !props.values?.title}
                sx={{ width: "207px", height: "65px" }}
              >
                {isLoading && <Loader style={{ color: "#fff" }} />}
                {!isLoading && <span>Get Started Now</span>}
              </Button>
              {/* <Button
                variant="outlined"
                sx={{ width: "207px", height: "65px" }}
              >
                View A Demo
              </Button> */}
            </div>
          </div>
        </div>
        <div className="EventAuth__preview">
          <SVG src="/static/auth.svg" />
        </div>
      </div>
    </div>
  );
};

export default withFormik({
  mapPropsToValues: () => ({ ...iniValues }),
  validationSchema,
})(EventAuth);
